import { SortingState, Updater } from '@tanstack/react-table';
import { create, StateCreator } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { FileUploadDTO } from '@/types/api-types';

export type SearchFileParam = {
  id: string;
  searchId: string;
  field: string;
  value: string;
  date: string;
  type: string;
};

interface FiltersUploadState {
  filterByIds: Record<keyof FileUploadDTO, string[]>;
  // Filter state
  sorting: SortingState;
  columnSortState: { [key: string]: 'asc' | 'desc' };

  setFilterByIds: (
    filterByIdsNew: Record<keyof FileUploadDTO, string[]>,
  ) => void;
  clearFilterByIds: (field: keyof FileUploadDTO) => void;
  clearFilterByIdsByValues: (
    field: keyof FileUploadDTO,
    values: string[],
  ) => void;
  setSorting: (updater: Updater<SortingState>) => void;
  setColumnSortState: (state: { [key: string]: 'asc' | 'desc' }) => void;
  clearFiltersAndSorting: () => void;
}

const storeCreator: StateCreator<
  FiltersUploadState,
  [['zustand/immer', never]]
> = (set, get) => ({
  sorting: [],
  columnSortState: {},
  filterByIds: {} as Record<keyof FileUploadDTO, string[]>,
  setFilterByIds: (filterByIdsNew: Record<keyof FileUploadDTO, string[]>) => {
    const { filterByIds } = get();
    set({ filterByIds: { ...filterByIds, ...filterByIdsNew } });
  },

  clearFilterByIds: (field: keyof FileUploadDTO) => {
    set((state) => ({
      filterByIds: Object.fromEntries(
        Object.entries(state.filterByIds || {}).filter(
          ([key]) => key !== field,
        ),
      ),
    }));
  },
  clearFilterByIdsByValues: (field: keyof FileUploadDTO, values: string[]) => {
    const { filterByIds } = get();
    const filteringField = filterByIds[field];
    if (filteringField) {
      const newFilterByIds = { ...filterByIds };
      newFilterByIds[field] = filteringField.filter(
        (filter) => !values.includes(filter),
      );
      set({ filterByIds: newFilterByIds });
    }
  },
  setSorting: (updater) => {
    set((state) => {
      state.sorting =
        typeof updater === 'function' ? updater(state.sorting) : updater;
    });
  },

  setColumnSortState: (sortState) => {
    set((state) => {
      state.columnSortState = sortState;
    });
  },

  clearFiltersAndSorting: () => {
    get().setSorting([]);
    get().setColumnSortState({});
  },
});

export const useFiltersUploadStore = create<FiltersUploadState>()(
  immer(storeCreator),
);
